// Monaco custom language registration for BAIQL
// Reference: https://microsoft.github.io/monaco-editor/monarch.html

export function registerBaiqlLanguage(monaco) {
  const languageId = 'baiql';

  const commands = [
    'BATCH_START','END_BATCH','LOAD','PROMPT','RUN','OUTPUT','GET','SET','IF','END_IF','LOOP','END_LOOP','STEP','MAKE_LOG','WRITE_LOG','ERROR','MODEL','FALLBACK','LIMIT','PATH_IN','PATH_OUT','GLOBAL'
  ];
  const types = [ 'FILE','DIR','REF','WORK' ];
  const modes = [ 'EXECUTE','VALIDATE','QUIET' ];
  const operators = [ 'AND','OR' ];
  const builtinVars = [
    'XACT','XACTTOT','TOTAL_FILES','FILENAME','FILEPATH','FILESIZE',
    'ERRFL','ERRCT','RETRYCT',
    'LOOPCT','STEPNUM','STEPTOT',
    'DATE','TIME','DATETIME','TIMESTAMP',
    'TOKSIN','TOKSOUT','TOKSTOT',
    'COSTCUR','COSTTOT',
    'EXECTM','ELAPTM',
    'CACHEHIT','CACHESAV',
    'BATCHID','BATCHTYPE',
    'MODELNAME','MODELTEMP','MODELTOPP',
    'PATHIN','PATHOUT',
    'USERID','USERNAME','SESSIONID',
    'SUCCESS','FAILED'
  ];
  const options = [ 'OPTIMIZE','FORMAT','MATCH','AGGREGATE','SEPARATE','SCREEN','CACHE','NOCACHE','TEMP','TOP_P','XACT','UNTIL','WHILE','BREAK','SKIP','RETRY','HALT','TRACK' ];

  if (!monaco.languages.getEncodedLanguageId(languageId)) {
    monaco.languages.register({ id: languageId });
  }

  monaco.languages.setLanguageConfiguration(languageId, {
    comments: { lineComment: '#' },
    brackets: [ ['{','}'], ['[',']'], ['(',')'] ]
  });

  monaco.languages.setMonarchTokensProvider(languageId, {
    defaultToken: '',
    ignoreCase: true,
    tokenizer: {
      root: [
        [/\s+/, ''],
        [/#.*/, 'comment'],
        [/"([^"\\]|\\.)*"/, 'string'],
        [new RegExp(`\\{(?:${builtinVars.join('|')})\\}`, 'i'), 'variable.predefined'],
        [/\{[A-Za-z_][A-Za-z0-9_]*\}/, 'variable'],
        [/\b\d+(?:\.\d+)?\b/, 'number'],
        [new RegExp(`\\b(${commands.join('|')})\\b`), 'keyword'],
        [new RegExp(`\\b(${types.join('|')})\\b`), 'type'],
        [new RegExp(`\\b(${modes.join('|')})\\b`), 'constant'],
        [new RegExp(`\\b(${options.join('|')})\\b`), 'operator'],
        [/\b(=|!=|<=|>=|<|>|AND|OR)\b/, 'operator'],
        [/[,()]/, 'delimiter'],
        [/[A-Za-z_][A-Za-z0-9_\-]*/, 'identifier']
      ]
    }
  });

  monaco.editor.defineTheme('baiql-dark', {
    base: 'vs-dark',
    inherit: true,
    rules: [
      { token: 'keyword', foreground: '4ea1ff' },    // bright blue
      { token: 'string', foreground: '98c379' },     // green
      { token: 'variable', foreground: 'e5c07b' },   // gold
      { token: 'variable.predefined', foreground: '56b6c2' }, // cyan
      { token: 'comment', foreground: '6b7280' },    // gray/dimmed
      { token: 'number', foreground: 'd19a66' },     // orange
      { token: 'type', foreground: 'bb9af7' },
      { token: 'constant', foreground: 'e0af68' },
      { token: 'operator', foreground: 'cfc9c2' },
      { token: 'identifier', foreground: 'c0caf5' }
    ],
    colors: {
      'editor.background': '#0b1020'
    }
  });

  // Avoid duplicate completions: use baiqlAutocomplete.js as the single provider
  const ENABLE_LANGUAGE_COMPLETIONS = false;
  if (ENABLE_LANGUAGE_COMPLETIONS) {
    monaco.languages.registerCompletionItemProvider(languageId, {
      triggerCharacters: [' ', '{'],
      provideCompletionItems: (model, position) => {
        const suggestions = [];

        // Command keywords
        for (const kw of commands) {
          suggestions.push({
            label: kw,
            kind: monaco.languages.CompletionItemKind.Keyword,
            insertText: kw + ' ',
            range: undefined
          });
        }

        // Snippets / common patterns
        const snippets = [
          {
            label: 'BATCH_START/END_BATCH',
            insertText: 'BATCH_START ${1:EXECUTE|VALIDATE|QUIET}\n  $0\nEND_BATCH',
            documentation: 'Begin a batch block with mode',
          },
          {
            label: 'LOAD file',
            insertText: 'LOAD FILE="${1:input.txt}" PATH_IN="${2:/data/in}"',
            documentation: 'Load a file from input path',
          },
          {
            label: 'PROMPT ref',
            insertText: 'PROMPT REF="${1:name}"',
            documentation: 'Select a prompt reference',
          },
          {
            label: 'MODEL',
            insertText: 'MODEL name="${1:gpt-4o-mini}"',
            documentation: 'Choose model. Optional: TEMP [0..1], TOP_P [0..1]. Scaling: OpenAI/Gemini temperature = TEMP×2; Anthropic/Perplexity use 0..1 direct.',
          },
          {
            label: 'RUN EXECUTE',
            insertText: 'RUN EXECUTE',
            documentation: 'Execute the current step',
          },
          {
            label: 'OUTPUT file',
            insertText: 'OUTPUT FILE="${1:output.txt}" PATH_OUT="${2:/data/out}"',
            documentation: 'Write output file',
          },
          {
            label: 'IF/END_IF',
            insertText: 'IF ${1:condition}\n  $0\nEND_IF',
            documentation: 'Conditional block',
          },
          {
            label: 'LOOP/END_LOOP',
            insertText: 'LOOP ${1:count}\n  $0\nEND_LOOP',
            documentation: 'Loop block',
          },
          {
            label: 'STEP',
            insertText: 'STEP ${1:label}',
            documentation: 'Define a step label',
          },
        ];
        for (const s of snippets) {
          suggestions.push({
            label: s.label,
            kind: monaco.languages.CompletionItemKind.Snippet,
            insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
            insertText: s.insertText,
            documentation: s.documentation,
            range: undefined
          });
        }

        // Built-in variables
        for (const v of builtinVars) {
          suggestions.push({
            label: `{${v}}`,
            kind: monaco.languages.CompletionItemKind.Variable,
            insertText: `{${v}}`,
            documentation: 'Built-in variable',
            range: undefined
          });
        }

        return { suggestions };
      }
    });
  }

  try { monaco.editor.setTheme('baiql-dark'); } catch {}
}


